import React, { useState, useCallback, useMemo } from "react";
import AsyncSelect from "react-select/async";
import axios from "../../../Axios";
import { Urls } from "../../../Urls";

const AutoComplete = ({
  value = "",
  onChange,
  onSelect,
  placeholder = "",
  getResults,

  isDisabled = false,
  minLength = 3,
  showNoSuggestions = true,
  className = "",
  classNamePrefix = "",
  customStyles = {},
}) => {
  const [noOptions, setNoOptions] = useState(false);

  // ===== DEBOUNCE PROMISE =====
  const debouncePromise = useMemo(() => {
    let timeout;

    return (func, delay) => {
      return (...args) => {
        clearTimeout(timeout);

        return new Promise((resolve) => {
          timeout = setTimeout(() => {
            resolve(func(...args));
          }, delay);
        });
      };
    };
  }, []);

  // ===== CUSTOM COMPONENTS =====
  const customComponents = useMemo(
    () => ({
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      LoadingIndicator: () => (
        <div style={{ paddingRight: "8px" }}>
          <div className="custom-spinner" />
        </div>
      ),
    }),
    []
  );

  // ===== TRANSFORM TRANSPORTER DATA INTO OPTIONS =====
  const transformToOptions = useCallback((data = []) => {
    return data.map((item) => ({
      label: item?.transporter_name || "",
      value: item?.transporter_name || "",
      full: item,
    }));
  }, []);

  // ===== FETCH TRANSPORTER OPTIONS =====
  const fetchOptions = useCallback(
    async (inputValue) => {
      if (!inputValue || inputValue.length < minLength) {
        return [];
      }

      try {
        const res = await axios.post(
          `${Urls.Autocomplete.Get.getTransporterName}`,
          { transporter_name: inputValue }
        );

        const data = res?.data?.transporter_data || [];
        setNoOptions(data.length === 0);

        return transformToOptions(data);
      } catch (error) {
        console.error("Transporter AutoComplete fetch error:", error);
        setNoOptions(true);
        return [];
      }
    },
    [minLength, transformToOptions]
  );

  // ===== LOAD OPTIONS WITH DEBOUNCE =====
  const loadOptions = useMemo(
    () => debouncePromise(fetchOptions, 500),
    [debouncePromise, fetchOptions]
  );

  // ===== HANDLE SELECT CHANGE =====
  const handleChange = useCallback(
    (selected) => {
      if (!selected) {
        onChange && onChange("");
        onSelect && onSelect(null);

        // Do not call getResults(null)
        return;
      }

      onChange && onChange(selected.value);
      onSelect && onSelect(selected.full);
      getResults && getResults(selected.full);
    },
    [onChange, onSelect, getResults]
  );

  // ===== NO OPTIONS MESSAGE =====
  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (!inputValue || inputValue.length < minLength) {
        return "Type to search transporters";
      }

      return showNoSuggestions ? "No transporter found" : null;
    },
    [minLength, showNoSuggestions]
  );

  // ===== SELECT VALUE =====
  const selectedValue = useMemo(() => {
    if (!value) return null;

    if (typeof value === "object") {
      return {
        label: value.label || value.transporter_name || "",
        value: value.value || value.transporter_name || "",
        full: value.full || value,
      };
    }

    return {
      label: value,
      value: value,
      full: null,
    };
  }, [value]);

  // ===== DEFAULT STYLES =====
  const selectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        borderRadius: 4,
        minHeight: 36,
        fontSize: "14px",
        fontFamily: "inherit",
        fontWeight: "400",
        color: "#495057",
        boxShadow: "none",
        borderColor: "#ccc",
        cursor: "text",
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: "14px",
        fontFamily: "inherit",
        fontWeight: "400",
        color: "#495057",
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: "14px",
        fontFamily: "inherit",
        color: "#495057",
        cursor: "text",
      }),
      valueContainer: (base) => ({
        ...base,
        padding: "2px 8px",
        cursor: "text",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: 200,
        overflowY: "auto",
      }),
      option: (base) => ({
        ...base,
        fontSize: 13,
        padding: "6px 10px",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
      }),
      ...customStyles,
    }),
    [customStyles]
  );

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      components={customComponents}
      isDisabled={isDisabled}
      value={selectedValue}
      className={className}
      classNamePrefix={classNamePrefix}
      placeholder={placeholder}
      styles={selectStyles}
      onChange={handleChange}
      noOptionsMessage={noOptionsMessage}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      isClearable
    />
  );
};

export default AutoComplete;